import { RivlLogo } from './components/RivlLogo';
import { ThemeToggle } from './components/ThemeToggle';
import { ArrowRight, Smartphone, ShieldCheck, Award } from 'lucide-react';

// External destinations — overridable via .env at build-time so the same
// codebase works in preview, staging and production.
const APP_URL = process.env.REACT_APP_APP_URL || 'https://app.rivl.run';
const ORGANIZER_URL = process.env.REACT_APP_ORGANIZER_URL || 'https://organizer.rivl.run';

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground rivl-noise">
      <header className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
          <RivlLogo size="lg" />
          <div className="flex items-center gap-3">
            <span className="rivl-label hidden md:inline">Race Identity &amp; Verification Layer</span>
            <ThemeToggle />
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-primary" />
          <div className="flex-1 bg-card" />
          <div className="flex-1 bg-[#1D4ED8]" />
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 pt-16 pb-10">
        <div className="grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <span className="rivl-label">v0.1 · MVP</span>
            <h1 className="mt-3 font-display font-black text-5xl md:text-7xl tracking-[-0.04em] leading-[0.95]">
              Structure, identity
              <br />
              and trust for
              <br />
              real-world races.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Run, race, and verify. RIVL brings clarity and integrity to physical events —
              start tracking your next race below.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-4">
          <a
            href={APP_URL}
            data-testid="enter-mobile"
            className="group rivl-card p-6 flex flex-col gap-6 hover:border-primary transition-colors"
          >
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-primary text-primary-foreground">
              <Smartphone className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-bold tracking-tight">Open RIVL</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Run, race, scan medals. The runner &amp; scanner experience.
              </p>
            </div>
            <div className="mt-auto flex items-center gap-2 text-sm font-medium group-hover:text-primary">
              Get started
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </a>

          <a
            href={ORGANIZER_URL}
            data-testid="enter-organizer"
            className="group rivl-card p-6 flex flex-col gap-6 hover:border-primary transition-colors"
          >
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[#1D4ED8] text-white">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-bold tracking-tight">For organizers</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Create official events, manage participants, run race-day operations.
              </p>
            </div>
            <div className="mt-auto flex items-center gap-2 text-sm font-medium group-hover:text-primary">
              Open organizer portal
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </a>

          <div className="rivl-card p-6 flex flex-col gap-6 bg-secondary/50 border-dashed">
            <div className="h-10 w-10 rounded-md flex items-center justify-center bg-foreground/10 text-foreground">
              <Award className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-bold tracking-tight">Verified finishes</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                GPS-tracked. Bib-linked. Medal-scanned. No more disputed results.
              </p>
            </div>
            <div className="mt-auto text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
              How it works ↘
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-6xl px-6 py-6 flex items-center justify-between text-xs text-muted-foreground">
          <span>© RIVL 2026</span>
          <a
            href={`${APP_URL}/auth`}
            data-testid="enter-app-link"
            className="text-primary hover:underline underline-offset-4"
          >
            Sign in →
          </a>
        </div>
      </footer>
    </div>
  );
}
